import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import TabNavigation from '../../components/ui/TabNavigation';

import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';
import MultiMemberTaskCard from './components/MultiMemberTaskCard';
import CreateTaskModal from './components/CreateTaskModal';
import TaskAnalytics from './components/TaskAnalytics';
import { taskService } from '../../services/taskService';
import { familyService } from '../../services/familyService';
import { useAuth } from '../../contexts/AuthContext';

const MultiMemberTaskCoordination = () => {
  const [familyMembers, setFamilyMembers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterMember, setFilterMember] = useState('all');
  const [sortBy, setSortBy] = useState('dueDate');
  const { userProfile } = useAuth();
  const { user } = useAuth();
  const [realtimeChannel, setRealtimeChannel] = useState(null);
  const [familyGroupId, setFamilyGroupId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Calculate overall progress for each task
  const tasksWithProgress = useMemo(() => {
    return tasks?.map(task => {
      const totalMembers = task?.assignedMembers?.length;
      const completedMembers = Object.values(task?.completionStatus || {})?.filter(status => status?.completed)?.length;
      const progress = totalMembers > 0 ? Math.round((completedMembers / totalMembers) * 100) : 0;
      const isComplete = progress === 100;
      
      return {
        ...task,
        progress,
        isComplete
      };
    });
  }, [tasks]);

  // Filter and sort tasks
  const filteredTasks = useMemo(() => {
    let filtered = tasksWithProgress;

    // Filter by status
    if (filterStatus === 'active') {
      filtered = filtered?.filter(task => !task?.isComplete);
    } else if (filterStatus === 'completed') {
      filtered = filtered?.filter(task => task?.isComplete);
    }

    // Filter by priority
    if (filterPriority !== 'all') {
      filtered = filtered?.filter(task => task?.priority === filterPriority);
    }

    // Filter by member
    if (filterMember !== 'all') {
      filtered = filtered?.filter(task => task?.assignedMembers?.includes(parseInt(filterMember)));
    }

    // Sort tasks
    filtered?.sort((a, b) => {
      if (sortBy === 'dueDate') {
        return new Date(a?.dueDate) - new Date(b?.dueDate);
      } else if (sortBy === 'priority') {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return priorityOrder?.[a?.priority] - priorityOrder?.[b?.priority];
      } else if (sortBy === 'progress') {
        return b?.progress - a?.progress;
      }
      return 0;
    });

    return filtered;
  }, [tasksWithProgress, filterStatus, filterPriority, filterMember, sortBy]);

  const handleCreateTask = async (taskData) => {
    try {
      const newTaskData = {
        family_group_id: familyGroupId,
        title: taskData?.title,
        description: taskData?.description,
        due_date: taskData?.dueDate,
        priority: taskData?.priority
      };

      let createdTask;
      if (taskData?.recurringConfig) {
        // Create recurring multi-member task
        createdTask = await taskService?.createRecurringMultiMemberTask(
          newTaskData,
          taskData?.assignedMembers,
          taskData?.recurringConfig
        );
      } else {
        // Create regular multi-member task
        createdTask = await taskService?.createMultiMemberTask(
          newTaskData,
          taskData?.assignedMembers
        );
      }

      // Reload tasks to get complete data with assignments
      await loadFamilyData();
      setShowCreateModal(false);
    } catch (err) {
      console.error('Error creating task:', err);
      setError(err?.message || 'Failed to create task');
    }
  };

  const handleUpdateTask = (taskId, updates) => {
    setTasks(tasks?.map(task => task?.id === taskId ? { ...task, ...updates } : task));
  };

  const handleDeleteTask = (taskId) => {
    setTasks(tasks?.filter(task => task?.id !== taskId));
  };

  const handleToggleMemberCompletion = (taskId, memberId) => {
    setTasks(tasks?.map(task => {
      if (task?.id === taskId) {
        const currentStatus = task?.completionStatus?.[memberId];
        return {
          ...task,
          completionStatus: {
            ...task?.completionStatus,
            [memberId]: {
              completed: !currentStatus?.completed,
              completedAt: !currentStatus?.completed ? new Date()?.toISOString() : null
            }
          }
        };
      }
      return task;
    }));
  };

  const handleSendMessage = (taskId, message) => {
    setTasks(tasks?.map(task => {
      if (task?.id === taskId) {
        return {
          ...task,
          chatMessages: [
            ...task?.chatMessages,
            {
              id: Date.now(),
              senderId: 1, // Default to first member
              content: message,
              timestamp: new Date()?.toISOString()
            }
          ]
        };
      }
      return task;
    }));
  };

  // Load family data and tasks
  useEffect(() => {
    if (user) {
      loadFamilyData();
    }
  }, [user]);

  const loadFamilyData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get family group
      const familyMember = await familyService?.getUserFamily();
      if (!familyMember || !familyMember?.family_groups) {
        setError('No family group found');
        setLoading(false);
        return;
      }

      setFamilyGroupId(familyMember?.family_groups?.id);

      // Get family members
      const members = await familyService?.getFamilyMembers(familyMember?.family_groups?.id);
      setFamilyMembers(members || []);

      // Get multi-member tasks
      const multiMemberTasks = await taskService?.getMultiMemberTasks(familyMember?.family_groups?.id);
      
      // Transform tasks to match component format
      const transformedTasks = multiMemberTasks?.map(task => ({
        id: task?.id,
        title: task?.title,
        description: task?.description,
        dueDate: task?.due_date,
        priority: task?.priority,
        assignedMembers: task?.assignments?.map(a => ({
          id: a?.member?.id,
          name: a?.member?.display_name,
          role: a?.member?.member_role,
          color: a?.member?.color,
          initials: a?.member?.initials,
          completed: a?.completed,
          completedAt: a?.completed_at
        })) || [],
        messages: task?.chat_messages?.map(m => ({
          id: m?.id,
          sender: m?.sender?.display_name,
          senderColor: m?.sender?.color,
          senderInitials: m?.sender?.initials,
          content: m?.content,
          timestamp: m?.created_at
        })) || [],
        isRecurring: task?.is_recurring,
        recurringConfig: task?.is_recurring ? {
          frequency: task?.recurrence_frequency,
          pattern: task?.recurrence_pattern,
          endDate: task?.recurrence_end_date
        } : null,
        createdAt: task?.created_at
      })) || [];

      setTasks(transformedTasks);
      setLoading(false);
    } catch (err) {
      console.error('Error loading family data:', err);
      setError(err?.message || 'Failed to load family data');
      setLoading(false);
    }
  };

  // Real-time subscription for task and assignment updates
  useEffect(() => {
    if (!familyGroupId) return;

    const channel = taskService?.subscribeToMultiMemberTasks(
      familyGroupId,
      (payload) => {
        if (payload?.eventType === 'INSERT') {
          setTasks(prev => [payload?.new, ...prev]);
        } else if (payload?.eventType === 'UPDATE') {
          setTasks(prev => prev?.map(task => 
            task?.id === payload?.new?.id ? { ...task, ...payload?.new } : task
          ));
        } else if (payload?.eventType === 'DELETE') {
          setTasks(prev => prev?.filter(task => task?.id !== payload?.old?.id));
        }
      }
    );

    setRealtimeChannel(channel);

    return () => {
      if (channel) {
        taskService?.unsubscribe(channel);
      }
    };
  }, [familyGroupId]);

  const statusOptions = [
    { value: 'all', label: 'All Tasks' },
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' }
  ];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ];

  const memberOptions = [
    { value: 'all', label: 'All Members' },
    ...familyMembers?.map(member => ({ value: member?.id?.toString(), label: member?.name }))
  ];

  const sortOptions = [
    { value: 'dueDate', label: 'Due Date' },
    { value: 'priority', label: 'Priority' },
    { value: 'progress', label: 'Progress' }
  ];

  return (
    <>
      <Helmet>
        <title>Multi-Member Task Coordination - Starling Family Planner</title>
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <TabNavigation />
        
        <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                  Multi-Member Task Coordination
                </h1>
                <p className="text-muted-foreground">
                  Collaborative tasks with individual tracking and team chat
                </p>
              </div>
              <Button
                onClick={() => setShowCreateModal(true)}
                iconName="plus"
                size="lg"
                className="hidden md:flex"
              >
                Create Task
              </Button>
            </div>

            {/* Mobile create button */}
            <Button
              onClick={() => setShowCreateModal(true)}
              iconName="plus"
              fullWidth
              className="md:hidden mb-4"
            >
              Create Task
            </Button>

            {/* Analytics */}
            <TaskAnalytics tasks={tasksWithProgress} familyMembers={familyMembers} />
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-xl p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Status
                </label>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e?.target?.value)}
                  options={statusOptions}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Priority
                </label>
                <Select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e?.target?.value)}
                  options={priorityOptions}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Member
                </label>
                <Select
                  value={filterMember}
                  onChange={(e) => setFilterMember(e?.target?.value)}
                  options={memberOptions}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Sort By
                </label>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e?.target?.value)}
                  options={sortOptions}
                />
              </div>
            </div>
          </div>

          {/* Task List */}
          <div className="space-y-4">
            {filteredTasks?.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-12 text-center">
                <Icon name="users" size={48} className="mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  No tasks found
                </h3>
                <p className="text-muted-foreground mb-4">
                  Create your first multi-member task to get started
                </p>
                <Button onClick={() => setShowCreateModal(true)} iconName="plus">
                  Create Task
                </Button>
              </div>
            ) : (
              filteredTasks?.map(task => (
                <MultiMemberTaskCard
                  key={task?.id}
                  task={task}
                  familyMembers={familyMembers}
                  onToggleMemberCompletion={handleToggleMemberCompletion}
                  onSendMessage={handleSendMessage}
                  onUpdateTask={handleUpdateTask}
                  onDeleteTask={handleDeleteTask}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Create Task Modal */}
      {showCreateModal && (
        <CreateTaskModal
          familyMembers={familyMembers}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateTask}
        />
      )}
    </>
  );
};

export default MultiMemberTaskCoordination;